import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmailDomain, EmailSendingAddress } from '../../../views/email/EmailTypes'
import PrimaryButton from 'components/buttons/PrimaryButton'
import SecondaryButton from 'components/buttons/SecondaryButton'
import { useGlobalContext } from 'GlobalContext'
import { useModal } from 'components/modals/ModalContext'
import { fetchData } from 'services/api/fetchData'
import { useAuthContext } from 'services/authentication/AuthenticationContext'
import TextInput from 'components/forms/basefields/TextInput'
import IconButton from 'components/buttons/IconButton'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsRotate, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons'
import { saveData } from 'services/api/saveData'

interface EmailDomainVerificationModalProps {
    itemId?: number
    refetchList?: () => void
}

const EmailDomainVerificationModal: React.FC<EmailDomainVerificationModalProps> = ({ 
    itemId, refetchList 
}) => {
    const { t } = useTranslation()
    const { setFloatingAlert } = useGlobalContext();
    const { handleLogout } = useAuthContext();
    const { revealModal, closeModal } = useModal();
    const [domain, setDomain] = useState<EmailDomain | null>(null);
    const [prefix, setPrefix] = useState('');
    const [loading, setLoading] = useState<string>('idle');
    const [sendingAddresses, setSendingAddresses] = useState<EmailSendingAddress[]>([]);
    const [newAddressPrefix, setNewAddressPrefix] = useState('');
    const [addressLoading, setAddressLoading] = useState<string>('idle');

    // Fetch the sending addresses of this domain
    const fetchSendingAddresses = async () => {
        if (!itemId) return;

        const response = await fetchData({
            apiObject: 'email_sending_address',
            params: { domain: itemId, deleted: false },
            handleLogout
        });

        setSendingAddresses(response?.results || []);
    };

    // Fetch the domain data
    useEffect(() => {
        const fetchDomain = async () => {
            // Get the data from the backend
            const response = await fetchData({
                apiObject: 'email_domain',
                itemId,
                handleLogout
            })

            // Set the response
            setDomain(response);
            setPrefix(response.default_from_prefix);

            // Reveal the modal
            revealModal();
        }
        if (itemId) fetchDomain()
        fetchSendingAddresses();
    }, [itemId]);

    // Handle adding a new sending address
    const handleAddSendingAddress = async () => {
        if (!newAddressPrefix.trim() || !domain) return;

        setAddressLoading('add');

        try {
            await saveData({
                apiObject: 'email_sending_address',
                method: 'post',
                data: { domain: domain.id, prefix: newAddressPrefix }
            });

            setNewAddressPrefix('');
            await fetchSendingAddresses();

            setFloatingAlert({ type: 'success' });
        } catch {
            setFloatingAlert({ type: 'danger', message: 'alert.general_error_message' });
        }

        setAddressLoading('idle');
    };

    // Handle deleting a sending address
    const handleDeleteSendingAddress = async (address: EmailSendingAddress) => {
        setAddressLoading(`delete-${address.id}`);

        try {
            await saveData({
                apiObject: 'email_sending_address',
                method: 'patch',
                itemId: address.id,
                data: { deleted: true }
            });

            await fetchSendingAddresses();

            setFloatingAlert({ type: 'success' });
        } catch {
            setFloatingAlert({ type: 'danger', message: 'alert.general_error_message' });
        }

        setAddressLoading('idle');
    };

    // Get the sending dns records
    const sendingRecords = domain?.dns_records?.filter(record => record.type !== 'MX') || [];

    // Get the reply (receiving) dns records, from the separately registered reply.<domain> receiving domain
    const replyRecords = domain?.receiving_dns_records || [];

    // Handle the domain verification check
    const handleCheck = async () => {
        // Start the button loader
        setLoading('check');

        try {
            // Check the domain verification state
            const response = await fetchData({
                apiUrl: `check_email_domain_verification/${domain?.id}`,
                handleLogout
            });

            // Unpack the result
            const result = response?.results?.[0];

            // Update the local domain state so both badges reflect the latest check
            if (result) {
                setDomain(prev => prev ? {
                    ...prev,
                    verified: result.verified ?? prev.verified,
                    receiving_verified: result.receiving_verified ?? prev.receiving_verified,
                } : prev);
            }

            // Show floating alerts based on the result
            if (result?.verified) {
                setFloatingAlert({
                    type: 'success',
                    message: 'email.email_domain.verified_success'
                });
            } else {
                setFloatingAlert({
                    type: 'warning',
                    message: 'email.email_domain.not_verified_try_later'
                });
                setLoading('idle');
                return;
            }

            // Refetch the list
            if (refetchList) {
                refetchList();
            }

            // Close the modal
            closeModal();

        } catch (error) {
            setFloatingAlert({
                type: 'danger',
                message: 'alert.general_error_message'
            });
        }

        setLoading('idle');
    };

    // Handle submit
    const handleSubmit = async () => {
        if (domain) {
            // Start button loader
            setLoading('save');

            try {
                // Save the data
                await saveData({
                    apiObject: 'email_domain',
                    method: 'patch',
                    itemId: domain.id,
                    data: { prefix: prefix }
                });

                // Show success alert
                setFloatingAlert({
                    type: 'success',
                });

                // Refetch the list
                if (refetchList) {
                    refetchList();
                }

                // Close the modal
                closeModal();

            } catch {
                setFloatingAlert({
                    type: 'danger',
                    message: 'alert.general_error_message'
                });
            }

            setLoading('idle');
        }
    };

    return (
        <div className='email-domain-verification-modal'>
            <div className='modal-header'>
                <h4>{t('email.email_domain.domain_modal_title', { domain_name: domain?.domain })}</h4>
                <div className='verification-header'>
                    <div>
                        <div className={`badge ${domain?.verified ? 'verified' : ''}`}>
                            {domain?.verified
                                ? t('email.email_domain.verified_label')
                                : t('email.email_domain.not_verified_label')}
                        </div>
                        {!domain?.verified && (
                            <IconButton
                                onClick={handleCheck}
                                icon={<FontAwesomeIcon icon={faArrowsRotate} />}
                                label="email.email_domain.check_domain_verification"
                                size="extra-small"
                                loading={loading === 'check'}
                            />
                        )}
                    </div>
                </div>
            </div>
            <p>
                {t('email.email_domain.verification_explanation')}
            </p>
            <div className='prefix-edit'>
                <label>
                    {t('email.email_domain.prefix_label')}
                </label>
                <div>
                    <TextInput
                        name='prefix'
                        placeholder='email.email_template.from_prefix_placeholder'
                        value={prefix}
                        onChange={(value) => setPrefix(value)}
                        shouldAutoFocus
                    />
                    <span>@{domain?.domain}</span>
                </div>
            </div>
            <div className='sending-addresses'>
                <h6>
                    {t('email.email_sending_address.object_name.plural')}
                </h6>
                <p>
                    {t('email.email_sending_address.modal_explanation')}
                </p>
                {sendingAddresses.length > 0 && (
                    <div className='sending-addresses-list'>
                        {sendingAddresses.map(address => (
                            <div key={address.id} className='sending-addresses-list-item'>
                                <span>{address.email}</span>
                                {!address.is_default && (
                                    <IconButton
                                        onClick={() => handleDeleteSendingAddress(address)}
                                        icon={<FontAwesomeIcon icon={faTrash} />}
                                        tooltipText='general.delete'
                                        size="extra-small"
                                        loading={addressLoading === `delete-${address.id}`}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                )}
                <div className='sending-addresses-add'>
                    <TextInput
                        name='new_sending_address_prefix'
                        placeholder='email.email_template.from_prefix_placeholder'
                        value={newAddressPrefix}
                        onChange={(value) => setNewAddressPrefix(value)}
                    />
                    <span>@{domain?.domain}</span>
                    <IconButton
                        onClick={handleAddSendingAddress}
                        icon={<FontAwesomeIcon icon={faPlus} />}
                        label='email.email_sending_address.add_button_label'
                        size="extra-small"
                        loading={addressLoading === 'add'}
                    />
                </div>
            </div>
            <div className='send-domain-records'>
                <h6>
                    {t('email.email_domain.send_domain_records_title')}
                    
                </h6>
                <p>
                    {t('email.email_domain.send_domain_records_explanation')}
                    
                </p>
                <table>
                    <thead>
                        <tr>
                            <th style={{ width: '80px' }}>
                                {t('email.email_domain.record_type')}
                            </th>
                            <th style={{ width: '180px' }}>
                                {t('email.email_domain.record_host')}
                            </th>
                            <th style={{ width: '450px' }}>
                                {t('email.email_domain.record_value')}
                            </th>
                            <th style={{ width: '100px' }}>
                                {t('email.email_domain.record_priority')}
                            </th>
                            <th style={{ width: '100px' }}>
                                {t('email.email_domain.record_ttl')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sendingRecords.map((record, index) => (
                            <tr key={index}>
                                <td>
                                    {record.type}
                                </td>
                                <td className='monospace-value'>
                                    {record.host || record.name || '@'}
                                </td>
                                <td className='monospace-value'>
                                    {record.value}
                                </td>
                                <td>
                                    {record.priority ?? '-'}
                                </td>
                                <td>
                                    {record.ttl ?? 3600}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className='reply-domain-records'>
                <h6>
                    {t('email.email_domain.reply_domain_records_title')}
                </h6>
                <div className='verification-header'>
                    <div>
                        <div className={`badge ${domain?.receiving_verified ? 'verified' : ''}`}>
                            {domain?.receiving_verified
                                ? t('email.email_domain.verified_label')
                                : t('email.email_domain.not_verified_label')}
                        </div>
                        {!domain?.receiving_verified && (
                            <IconButton
                                onClick={handleCheck}
                                icon={<FontAwesomeIcon icon={faArrowsRotate} />}
                                label="email.email_domain.check_domain_verification"
                                size="extra-small"
                                loading={loading === 'check'}
                            />
                        )}
                    </div>
                </div>
                <p>
                    {t('email.email_domain.reply_domain_records_explanation', { reply_to: `${prefix}@reply.${domain?.domain}` })}
                </p>
                <table>
                    <thead>
                        <tr>
                            <th style={{ width: '80px' }}>
                                {t('email.email_domain.record_type')}
                            </th>
                            <th style={{ width: '180px' }}>
                                {t('email.email_domain.record_host')}
                            </th>
                            <th style={{ width: '450px' }}>
                                {t('email.email_domain.record_value')}
                            </th>
                            <th style={{ width: '100px' }}>
                                {t('email.email_domain.record_priority')}
                            </th>
                            <th style={{ width: '100px' }}>
                                {t('email.email_domain.record_ttl')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {replyRecords.map((record, index) => (
                            <tr key={index}>
                                <td>
                                    {record.type}
                                </td>
                                <td className='monospace-value'>
                                    {record.host}
                                </td>
                                <td className='monospace-value'>
                                    {record.value}
                                </td>
                                <td>
                                    {record.priority ?? 10}
                                </td>
                                <td>
                                    {record.ttl ?? 3600}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="footer">
                <div className="buttons-right">
                    <SecondaryButton
                        onClick={closeModal}
                        label="general.close"
                        size="small"
                    />
                    <PrimaryButton
                        onClick={handleSubmit}
                        label="general.save"
                        size="small"
                        loading={loading === 'save'}
                    />
                </div>
            </div>
        </div>
    )
}

export default EmailDomainVerificationModal